import React from 'react';
import { HasChildren, AuthInfo, User } from "@shapeable/types";
import { AuthProvider as CoreAuthProvider, useSite } from '@shapeable/ui';

import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { get, trimStart, startCase, pickBy } from 'lodash';
import { navigate } from 'gatsby';
import AuthorizedApolloProvider from './authorized-apollo-provider';


// we need an inner provider to be able to make use of the info via the hook.
// that is, it needs to be wrapped with the Auth0 provider first (below).

const InnerAuthProvider: React.FC<{ loginReturnTo: string, logoutReturnTo: string }> = ({ children, loginReturnTo, logoutReturnTo }) => {
  const auth0 = useAuth0();
  
  const logout = async () => {
    auth0.logout({
      logoutParams: { returnTo: logoutReturnTo }
    });
  };

  
  const login = async (options?: { url: string, path: string }) => {
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    /* @ts-ignore */
    // auth0.loginWithRedirect(pickBy({
    //   openUrl: !isLocalDev && (async (url) => { window.open(options?.url || loginReturnTo, '_self') }),
    //   appState: isLocalDev && ({ returnTo: options?.path || loginReturnTo }),
    // }));

    /* @ts-ignore */
    auth0.loginWithRedirect(pickBy({
      appState: ({ returnTo: options?.path || loginReturnTo }),
    }));

  }
  
  const getTokenSilently = auth0.getAccessTokenSilently;

  const auth0User = (auth0.user || {});
  
  let nameParts = auth0User.name?.split(" ") || [];

  if (nameParts.length < 2) {
    nameParts = auth0User.name?.split(".") || [];
  }

  if (nameParts.length < 2) {
    nameParts = auth0User.nickname?.split(".") || [];
  }

  if (nameParts.length < 2) {
    nameParts = auth0User.email?.split(".") || [];
  }

  const user: User = {
    ...auth0User,
    id: auth0User.sub,
    givenName: auth0User.given_name || startCase(nameParts?.[0]) || auth0User.nickname || auth0User.email,
    familyName: auth0User.family_name || startCase(nameParts?.[1]),
  };

  const value: AuthInfo = {
    /* @ts-ignore */
    isAvailable: true,
    isAuthenticated: auth0.isAuthenticated,
    user,
    getTokenSilently,
    login,
    logout,
  };

  // also provide an Authorized Apollo Provider to allow authorized requests to the platform API

  return ( 
    /* @ts-ignore */
    <CoreAuthProvider value={value}>
    <AuthorizedApolloProvider>
      {children}
    </AuthorizedApolloProvider>
    </CoreAuthProvider>
  )
};


export const AuthProvider: React.FC<HasChildren & { pageContext?: any }> = ({ children, pageContext }) => {

  const joinPaths = (...parts: string[]) => parts.map((part) => trimStart(part, '/')).join('/');

  let siteUrl = get(pageContext, 'site.url', '');

  if (typeof window !== 'undefined') {
    siteUrl = window.location.origin;
  }

  const loginReturnTo = (siteUrl && process.env.AUTH_LOGIN_RETURN_PATH) ? joinPaths(siteUrl, process.env.AUTH_LOGIN_RETURN_PATH) : (process.env.AUTH_REDIRECT_URI || siteUrl);
  const logoutReturnTo = (siteUrl && process.env.AUTH_LOGOUT_RETURN_PATH) ? joinPaths(siteUrl, process.env.AUTH_LOGOUT_RETURN_PATH) : siteUrl;

  if (process.env.AUTH0_DOMAIN && process.env.AUTH0_CLIENT_ID) {
    return (
      <Auth0Provider
        domain={process.env.AUTH0_DOMAIN}
        clientId={process.env.AUTH0_CLIENT_ID}
        onRedirectCallback={(appState) => {
          if (appState?.returnTo) {
            navigate(appState.returnTo);
          }
        }}
        authorizationParams={{
          audience: process.env.AUTH0_AUDIENCE,
          redirect_uri: loginReturnTo
        }}
      >
        <InnerAuthProvider 
          loginReturnTo={loginReturnTo}
          logoutReturnTo={logoutReturnTo}>
          {children}
        </InnerAuthProvider>
      </Auth0Provider>
    );
  }

  return <CoreAuthProvider value={{ isAvailable: false }}>{children}</CoreAuthProvider>;
};